import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ClipboardCheck, Plus, Search, Calendar, MoreVertical, 
  Edit, Trash2, Eye, AlertCircle, Clock, CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  getAuditEngagements, createAuditEngagement, deleteAuditEngagement,
  AUDIT_TYPES, AUDIT_STATUSES, getAuditTypeIcon
} from '@/services/audit';
import { toast } from 'sonner';

const AuditEngagementsPage = () => {
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    audit_type: 'internal',
    scope_description: '',
    lead_auditor: '',
    planned_start_date: '',
    planned_end_date: ''
  });

  useEffect(() => {
    loadEngagements();
  }, [search, typeFilter, statusFilter]);

  const loadEngagements = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (typeFilter !== 'all') params.audit_type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const data = await getAuditEngagements(params);
      setEngagements(data.engagements || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to load engagements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await createAuditEngagement({
        ...formData,
        planned_start_date: new Date(formData.planned_start_date).toISOString(),
        planned_end_date: new Date(formData.planned_end_date).toISOString()
      });
      toast.success('Audit engagement created successfully');
      setShowCreateDialog(false);
      resetForm();
      loadEngagements();
    } catch (error) {
      toast.error('Failed to create engagement');
    }
  };

  const handleDelete = async (engagement) => {
    if (window.confirm(`Delete audit "${engagement.title}"?`)) {
      try {
        await deleteAuditEngagement(engagement.id);
        toast.success('Engagement deleted');
        loadEngagements();
      } catch (error) {
        toast.error('Failed to delete engagement');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      audit_type: 'internal',
      scope_description: '',
      lead_auditor: '',
      planned_start_date: '',
      planned_end_date: ''
    });
  };

  const getStatusBadge = (status) => {
    const stat = AUDIT_STATUSES.find(s => s.value === status);
    const colors = {
      planned: 'bg-gray-100 text-gray-700',
      in_progress: 'bg-blue-100 text-blue-700',
      review: 'bg-yellow-100 text-yellow-700',
      completed: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
        {stat?.label || status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const t = AUDIT_TYPES.find(a => a.value === type);
    return (
      <span className="px-2 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 flex items-center gap-1">
        <span>{t?.icon || '📋'}</span>
        {t?.label || type}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Engagements</h1>
          <p className="text-gray-500 mt-1">Manage audit projects and engagements</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreateDialog(true); }} className="gap-2">
          <Plus className="w-4 h-4" />
          New Engagement
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ClipboardCheck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Engagements</p>
                <p className="text-2xl font-bold">{total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">In Progress</p>
                <p className="text-2xl font-bold">
                  {engagements.filter(e => e.status === 'in_progress').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Completed</p>
                <p className="text-2xl font-bold">
                  {engagements.filter(e => e.status === 'completed').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Planned</p>
                <p className="text-2xl font-bold">
                  {engagements.filter(e => e.status === 'planned').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search engagements..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Audit Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {AUDIT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {AUDIT_STATUSES.map(stat => (
                  <SelectItem key={stat.value} value={stat.value}>{stat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Engagements Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead Auditor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Findings</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : engagements.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No engagements found. Click "New Engagement" to create one.
                    </td>
                  </tr>
                ) : (
                  engagements.map(engagement => (
                    <tr key={engagement.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-4 py-3 text-sm font-mono text-gray-600">{engagement.engagement_id}</td>
                      <td className="px-4 py-3">
                        <Link to={`/grc/audit/engagements/${engagement.id}`} className="font-medium text-blue-600 hover:underline">
                          {engagement.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3">{getTypeBadge(engagement.audit_type)}</td>
                      <td className="px-4 py-3">{getStatusBadge(engagement.status)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{engagement.lead_auditor}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(engagement.planned_start_date)} - {formatDate(engagement.planned_end_date)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs font-medium">
                          {engagement.findings_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/grc/audit/engagements/${engagement.id}`}>
                                <Eye className="w-4 h-4 mr-2" /> View Details
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(engagement)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Audit Engagement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-medium">Title *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Audit engagement title"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description"
                  className="w-full p-2 border rounded-md min-h-[80px]"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Audit Type *</label>
                <Select value={formData.audit_type} onValueChange={(v) => setFormData({ ...formData, audit_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUDIT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Lead Auditor *</label>
                <Input
                  value={formData.lead_auditor}
                  onChange={(e) => setFormData({ ...formData, lead_auditor: e.target.value })}
                  placeholder="Lead auditor name"
                />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium">Scope Description *</label>
                <textarea
                  value={formData.scope_description}
                  onChange={(e) => setFormData({ ...formData, scope_description: e.target.value })}
                  placeholder="Describe the audit scope, objectives, and areas to be covered"
                  className="w-full p-2 border rounded-md min-h-[100px]"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Start Date *</label>
                <Input
                  type="date"
                  value={formData.planned_start_date}
                  onChange={(e) => setFormData({ ...formData, planned_start_date: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">End Date *</label>
                <Input
                  type="date"
                  value={formData.planned_end_date}
                  onChange={(e) => setFormData({ ...formData, planned_end_date: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              disabled={!formData.title || !formData.scope_description || !formData.lead_auditor || !formData.planned_start_date || !formData.planned_end_date}
            >
              Create Engagement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditEngagementsPage;
